export interface CustomerReview {
  review: string
  name: string
  title: string
  company: string
  img: string
  logo: string
  url: string
  sectors: string[]
}

export const REVIEW_SOURCE_NAME = 'Trustpilot'
export const REVIEW_SOURCE_URL = 'https://uk.trustpilot.com/review/switchboardfree.co.uk'

export interface ReviewAggregate {
  ratingValue: string
  reviewCount: string
  bestRating: string
  worstRating: string
}

/**
 * Verified aggregate rating from REVIEW_SOURCE_NAME.
 *
 * Set this to the exact TrustScore and review count shown on the Trustpilot
 * profile, and keep it in step with that page. While it is null, the site
 * links to the profile without asserting a score. Never publish an unverified
 * figure here: it feeds customer-facing copy. Structured data carries no
 * aggregateRating at all (see getAggregateRatingProp in components/JsonLd.js).
 */
export const REVIEW_AGGREGATE: ReviewAggregate | null = {
  ratingValue: '4.2',
  reviewCount: '489',
  bestRating: '5',
  worstRating: '1'
}

// Real, named SwitchboardFREE customers. Do not add a quote here unless it has
// been given by a real customer and we hold their permission to publish it.
export const FEATURED_REVIEWS: CustomerReview[] = [
  {
    review: 'It makes a huge difference to work with partners and suppliers who understand the needs of a small business, and in this regard SwitchboardFREE is a great example so I’m happy to recommend their services whenever I can.',
    name: 'Chris Williams',
    title: 'Technical Director',
    company: 'Imaginative Software Ltd',
    img: '/images/reviews/chris-w.jpg',
    logo: '/images/reviews/logos/imaginative.jpg',
    url: 'www.imaginativesoftware.co.uk',
    sectors: ['technology', 'professional']
  },
  {
    review: 'Well!!! What can I say........simply outstanding service. I would 100% without any hesitation recommend this company.',
    name: 'Connor Glass',
    title: 'Director',
    company: 'Hillhead Security',
    img: '/images/reviews/connor-g.jpg',
    logo: '/images/reviews/logos/hillhead.jpg',
    url: 'www.hillheadsecurity.com',
    sectors: ['trades', 'security']
  },
  {
    review: 'This is a great service all round! The tech works like a dream, super-professional inbound impression of my business, coupled with top drawer customer support, it is a real winner.',
    name: 'Toby Turner',
    title: 'Director',
    company: 'Juice Commercial Finance',
    img: '/images/reviews/toby-t.jpg',
    logo: '/images/reviews/logos/juice.jpg',
    url: 'www.juicecommercial.com',
    sectors: ['finance', 'professional']
  },
  {
    review: 'SwitchboardFREE have been excellent from the first moment I contacted them. I totally recommend them for both their services and their customer service team.',
    name: 'David Conrich',
    title: 'Director',
    company: 'France Skiing',
    img: '/images/reviews/david-c.jpg',
    logo: '/images/reviews/logos/france.jpg',
    url: 'www.france-skiing.net',
    sectors: ['travel']
  },
  {
    review: 'Very good service.',
    name: 'Lloydie Gardiner',
    title: 'Holiday Experience Specialist',
    company: 'Not Just Travel',
    img: '/images/reviews/lloydie-g.jpg',
    logo: '/images/reviews/logos/travel.jpg',
    url: 'www.notjusttravel.co.uk',
    sectors: ['travel']
  },
  {
    review: 'The solution is brilliant for a small business, and the people are even better. We knew we were in good hands from the first moment.',
    name: 'Gavin Casey',
    title: 'Director',
    company: 'Index Property Information',
    img: '/images/reviews/gavin-c.jpg',
    logo: '/images/reviews/logos/index.jpg',
    url: 'www.indexpi.co.uk',
    sectors: ['property', 'professional']
  },
  {
    review: 'Great idea - great service and a great advert for a small business to give the professional impression!',
    name: 'Neil Mathias',
    title: 'Director',
    company: 'Compliance CRG',
    img: '/images/reviews/neil-m.jpg',
    logo: '/images/reviews/logos/compliance.jpg',
    url: 'www.complianceCRG.com',
    sectors: ['compliance', 'professional']
  },
  {
    review: 'SwitchboardFREE is the best thing for my business, if I miss calls or a message I get an email and the recording left! The system sounds professional and fab music on-hold, definitely a must for any business!',
    name: 'Justine Conway',
    title: 'Owner',
    company: 'Paparazzi Hire',
    img: '/images/reviews/justine-c.jpg',
    logo: '/images/reviews/logos/paparazzi.jpg',
    url: 'www.hirepaparazzi.co.uk',
    sectors: ['retail', 'events']
  },
  {
    review: 'I\'ve been using SwitchboardFree for many years now and have found the service to be invaluable for my small business. The ability to have a dedicated telephone number that has a switchboard with office hours set to it, helps to keep my work and private life separate.',
    name: 'Mark Jones',
    title: 'Owner',
    company: 'MDJ Creative',
    img: '/images/reviews/mark-j.jpg',
    logo: '/images/reviews/logos/mdj.jpg',
    url: 'www.mdjcreative.co.uk',
    sectors: ['creative', 'professional']
  },
  {
    review: 'Excellent service and prices. Very pleasant and was able to answer all my questions. Highly recommended 5*',
    name: 'Lee Foster',
    title: 'Owner',
    company: 'RF Digital Solutions',
    img: '/images/reviews/lee-f.jpg',
    logo: '/images/reviews/logos/rfdigital.jpg',
    url: 'www.rfdigitalsolutions.com',
    sectors: ['technology', 'trades']
  }
]

/**
 * Returns a deterministic selection of featured reviews.
 * Reviews from the requested sectors are returned first, then the remaining
 * reviews in their published order are used to make the requested count up.
 */
export const getFeaturedReviews = (sectors: string[] = [], count = 3): CustomerReview[] => {
  if (count <= 0) {
    return []
  }

  const matching = sectors.length > 0
    ? FEATURED_REVIEWS.filter(review => review.sectors.some(sector => sectors.includes(sector)))
    : []

  const remaining = FEATURED_REVIEWS.filter(review => !matching.includes(review))

  return [...matching, ...remaining].slice(0, count)
}
